import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import {
  selectAnonymousUsers,
  selectRoomSession,
  selectRoomSessionHasTranscription,
  selectUsersWithClientRole,
} from '../../../../../store/meetingSummary/roomSession'
import { User } from '../../../../../models/User'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomAndAIFeatureTrackingDetails } from '../../tracking'
import { Clients } from '../Common/Clients'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'
import { AiFeature } from '../../../../../models/AiFeature'
import { FormResult } from './FormResult'
import { getUserIdFromFormResultKey, selectFormResultsForAiFeatureId, selectFormScorecardForAiFeature } from '../../../../../store/meetingSummary/forms'
import { AIAgentResult } from '../../../../../models/AIAgent'
import { useExecuteForm } from './hooks'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'

interface FormResultViewProps {
  aiFeature: AiFeature
  collapsed: boolean
}

export const FormResultView: React.FC<FormResultViewProps> = ({ aiFeature, collapsed }) => {
  const roomSession = useAppSelector(selectRoomSession)
  const formResults = useAppSelector((state) => selectFormResultsForAiFeatureId(state, aiFeature.id))
  const clientUsers = useAppSelector(selectUsersWithClientRole)
  const anonymousUsers = useAppSelector(selectAnonymousUsers)
  const currentUser = useAppSelector(selectCurrentUser)
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const [formToDisplay, setFormToDisplay] = useState<AIAgentResult | null>(null)
  const [currentClient, setCurrentClient] = useState<User | null>(null)
  const { tracked } = useTracking()
  const selectedScorecard = useAppSelector((state) => selectFormScorecardForAiFeature(state, aiFeature.id))

  const executeForm = useExecuteForm()
  const isUserLevel = aiFeature.generalConfig?.userLevel

  useEffect(() => {
    if (!isUserLevel) {
      return
    }
    const currentUser = clientUsers[0] ?? anonymousUsers[0]
    if (currentUser) {
      setCurrentClient(currentUser)
    }
  }, [isUserLevel, clientUsers, anonymousUsers])

  useEffect(() => {
    if (!formResults.length) {
      return
    }
    if (!isUserLevel) {
      setFormToDisplay(formResults[0])
    } else if (currentClient) {
      setFormToDisplay(formResults.find((r) => getUserIdFromFormResultKey(r.resultKey) === currentClient.id) ?? null)
    }
  }, [formResults, isUserLevel, currentClient])

  const _handleRun = async () => {
    await executeForm(aiFeature.id)
  }

  const handleRun = tracked(_handleRun, {
    eventName: TrackEvent.FORM_MANUALLY_EXECUTED,
    eventDetails: getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
  })

  if (collapsed) {
    return null
  }

  const saveEventDetails = {
    ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
    contactId: currentClient?.id,
    formType: 'meeting',
  }

  const exportEventDetails = {
    ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
    type: 'formResult',
    contactId: currentClient?.id,
    formType: 'meeting',
  }

  return (
    <div>
      {formToDisplay ? (
        <div>
          {isUserLevel && currentClient && (
            <div className='pb-3 gap-2'>
              <Clients users={[...clientUsers, ...anonymousUsers]} userClicked={setCurrentClient} activeClient={currentClient} />
            </div>
          )}
          <div className='pb-3'>
            <FormResult
              key={formToDisplay.id}
              formResult={formToDisplay}
              user={currentClient}
              aiFeature={aiFeature}
              currentUser={currentUser}
              scorecard={selectedScorecard}
              saveEventDetails={saveEventDetails}
              exportEventDetails={exportEventDetails}
            />
          </div>
        </div>
      ) : (
        <div className='no-result-container d-flex p-3 gap-2 align-items-center'>
          <ScopedBlock
            scope={{
              scope: 'aifeature:execute',
              superScope: ['aifeature:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Tooltip title={!hasTranscription ? 'Meeting has no transcription' : undefined}>
              <Button variant='primary' className='py-1 px-2' onClick={handleRun} disabled={!hasTranscription}>
                <i className='bi bi-arrow-counterclockwise' />
                Run
              </Button>
            </Tooltip>
          </ScopedBlock>
          <span className='paragraph-medium'> the form to view results</span>
        </div>
      )}
    </div>
  )
}
